import ProductName from "@/base-components/ProductName";
import React from 'react';
import { RoutePath, SPACING } from '../../generic/config';
import { Context } from '../../App';
import { QueryKeys } from '../../api/base/react-query';
import { getOrder, searchForOrders } from '../../api/order-controller';
import { useQuery } from 'react-query';
import Table, { TableProps } from '../../base-components/Table';
import { useNavigate } from 'react-router-dom';
import EditorParent from '@/base-components/EditorParent';
import { CustomerNameCell } from '../customer/CustomerSearchPage';
import { getWorksiteFullName } from '@/model/worksite';
import { OrderHit } from '@/model/order-hit.schema';
import { Button, Spinner, Table as NativeTable, Flex, Spacer, Box } from '@chakra-ui/react';
import { ORDER_STATUS_OPTIONS, OrderStatus, SORT_ORDER_OPTIONS } from '@/model/order';
import SearcherNew, { SearcherFilters, DropdownFilterType, DateFilterType } from '@/generic/SearcherNew';
import { Translations } from '@/generic/Translations';
import Text from '@/base-components/Text';
import { isEmptyDate } from '@/generic/date-utils';
import Icon, { Icons } from '@/base-components/Icons';
import useDialog from '@/base-components/hooks/useDialog';
import Order from '@/model/order';

const defaultDateFrom = (() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
})();

type OrderFilter = SearcherFilters<{
    status: DropdownFilterType,
    sortOrder: DropdownFilterType,
    scheduledDateFrom: DateFilterType,
    scheduledDateTo: DateFilterType,
}>;

const ProductsCell: React.FC<{ row: OrderHit, translations: Translations }> = ({ row, translations }) => {
    const { openDialog, closeDialog } = useDialog();
    const t = translations.model.order;
    const hasAnyProducts = row.totalOrderedProductCount + row.totalActualProductCount > 0;

    const handleInfoClick = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        openDialog({
            title: t.productSummary.title,
            size: "md",
            body: <OrderSummaryBody orderId={row.id} translations={translations} />,
            buttons: [{ title: translations.common.ok, onClick: () => closeDialog() }],
        });
    };

    if (!hasAnyProducts) {
        return t.noProducts;
    }

    return (
        <Flex gap={SPACING} minW={`20ch`} alignItems="center" mr={SPACING}>
            <Box gap={SPACING} whiteSpace="nowrap" alignItems="center">
                <Text>
                    {t.orderedProducts.replace("{count}", String(row.totalOrderedProductCount))}
                </Text>
                <Text>
                    {t.receivedProducts.replace("{count}", String(row.totalActualProductCount))}
                </Text>
            </Box>
            <Spacer />
            <Icon icon={Icons.Info} cursor="pointer" onClick={handleInfoClick} />
        </Flex>
    );
};

const OrderSummaryBody: React.FC<{ orderId: number, translations: Translations }> = ({ orderId, translations }) => {
    const [order, setOrder] = React.useState<Order | null>(null);
    const [loading, setLoading] = React.useState(true);
    const t = translations.model.order;

    React.useEffect(() => {
        getOrder(orderId).then(o => {
            setOrder(o);
            setLoading(false);
        });
    }, [orderId]);

    if (loading) return <Spinner />;
    if (!order) return null;

    return (
        <NativeTable.Root size="sm">
            <NativeTable.Header>
                <NativeTable.Row>
                    <NativeTable.ColumnHeader>{t.productSummary.product}</NativeTable.ColumnHeader>
                    <NativeTable.ColumnHeader>{t.productSummary.orderedQuantity}</NativeTable.ColumnHeader>
                    <NativeTable.ColumnHeader>{t.productSummary.actualQuantity}</NativeTable.ColumnHeader>
                </NativeTable.Row>
            </NativeTable.Header>
            <NativeTable.Body>
                {order.orderRows.map((row) => (
                    <NativeTable.Row key={row.id}>
                        <NativeTable.Cell><ProductName code={row.product.code} icon={row.product.taskIcon} /></NativeTable.Cell>
                        <NativeTable.Cell>{row.orderedQuantity}</NativeTable.Cell>
                        <NativeTable.Cell>{row.actualQuantity}</NativeTable.Cell>
                    </NativeTable.Row>
                ))}
                {order.orderRows.length === 0 && (
                    <NativeTable.Row>
                        <NativeTable.Cell colSpan={3} textAlign="center">-</NativeTable.Cell>
                    </NativeTable.Row>
                )}
            </NativeTable.Body>
        </NativeTable.Root>
    );
};

const OrderSearchPage: React.FC = () => {
    const { translations } = React.useContext(Context);
    const navigate = useNavigate();
    const [page, setPage] = React.useState(0);

    const [filter, setFilter] = React.useState<OrderFilter>({
        keywords: "",
        status: {
            placeholder: "",
            kind: "dropdown",
            items: (t) => ORDER_STATUS_OPTIONS(t),
            value: null,
            title: (t) => t.model.order.status,
        },
        scheduledDateFrom: {
            kind: "date",
            value: defaultDateFrom,
            title: (t) => t.model.order.scheduledDateFrom,
        },
        scheduledDateTo: {
            kind: "date",
            value: '',
            title: (t) => t.model.order.scheduledDateTo,
        },
        sortOrder: {
            placeholder: "",
            kind: "dropdown",
            items: (t) => SORT_ORDER_OPTIONS(t),
            value: SORT_ORDER_OPTIONS(translations)[0],
            title: (t) => t.model.order.sortOrder,
        },
    });

    // A changed filter starts a fresh result set from page 0.
    React.useEffect(() => {
        setPage(0);
    }, [filter.keywords, filter.status.value, filter.scheduledDateFrom.value, filter.scheduledDateTo.value, filter.sortOrder.value]);

    const doSearch = async () => {
        return await searchForOrders(
            filter.keywords,
            filter.status.value?.id as OrderStatus ?? null,
            filter.scheduledDateFrom.value,
            filter.scheduledDateTo.value,
            filter.sortOrder.value?.id as string ?? null,
            page,
        );
    };
    const orderHits = useQuery([QueryKeys.orders, page], doSearch);

    const tableProps: TableProps<OrderHit> = {
        columns: [
            { title: translations.model.order.id, scale: 0.5, getter: (r) => r.id },
            { title: translations.model.order.status, scale: 0.5, getter: (r) => <strong>{translations.model.order.statusOptions[r.status]}</strong> },
            { title: translations.model.order.externalId, scale: 1, getter: (r) => r.externalId },
            { title: translations.model.worksite.worksite, scale: 2, getter: (r) => getWorksiteFullName(r.worksiteHit) },
            { title: translations.model.customer.customer, scale: 2, getter: (r) => CustomerNameCell({ row: r.worksiteHit.customerHit }) },
            { title: translations.model.order.products, scale: 0.1, getter: (r) => <ProductsCell row={r} translations={translations} /> },
            { title: translations.model.order.scheduledDate, scale: 1, getter: (r) => isEmptyDate(r.scheduledDate) ? '-' : r.scheduledDate },
            { title: translations.model.order.completionDate, scale: 1, getter: (r) => isEmptyDate(r.completionDate) ? '-' : r.completionDate },
        ],
        rows: orderHits?.data?.items,
        isLoading: orderHits.isLoading,
        href: (row) => `${RoutePath.Orders}/${row.id}`,
    };

    return (
        <EditorParent title={translations.model.order.search.title} description={translations.model.order.search.description}>
            <SearcherNew filters={filter} setFilters={setFilter} {...translations.model.order.search} query={orderHits}>
                <Button onClick={() => navigate(`${RoutePath.Orders}/new`)}>{translations.common.registerNew}</Button>
            </SearcherNew>
            <Table tableProps={tableProps} />
            <Pager page={page} setPage={setPage}
                total={orderHits.data?.total ?? 0} size={orderHits.data?.size ?? 0}
                count={orderHits.data?.items.length ?? 0} />
        </EditorParent>
    );
};

/** Prev/Next pager over a backend-paginated result set. */
export const Pager = ({ page, setPage, total, size, count }: {
    page: number, setPage: (updater: (p: number) => number) => void,
    total: number, size: number, count: number,
}) => {
    if (total === 0) return null;
    const from = page * (size || 1) + 1;
    const to = page * (size || 1) + count;
    const hasNext = (page + 1) * (size || 1) < total;
    return (
        <Flex justifyContent="center" alignItems="center" gap={SPACING} mt={SPACING}>
            <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>‹</Button>
            <Text>{`${from}–${to} of ${total}`}</Text>
            <Button size="sm" variant="outline" disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>›</Button>
        </Flex>
    );
};

export default OrderSearchPage;

/**
 * Warns the user if the maximum number of search hits has been reached.
 */
export const WarnMaxSearchHits = ({ translations, currentCount }: { translations: Translations, currentCount: number }) => {
    if (currentCount >= 250) {
        return <Text textAlign={"center"}>{` ${translations.maxSearchHitsWarning}`}</Text>;
    }

    return null;
};
